/**
 * Core type definitions for the backtesting system
 */

import { z } from 'zod';
import { SymbolDataCoverageSchema } from './data-lifetime.js';

// ============================================================================
// Timeframe
// ============================================================================

export const TimeframeSchema = z.enum([
  '1m', '5m', '15m', '20m', '30m',
  '1h', '4h', '12h', '18h',
  '1d', '3d', '1w',
]);
export type Timeframe = z.infer<typeof TimeframeSchema>;

/**
 * Canonical list of all supported timeframe values (in ascending duration order).
 * Use this instead of re-declaring the enum literal list at each call site
 * (API route schemas, chat tool JSON schemas, frontend selectors, etc).
 */
export const ALL_TIMEFRAMES = TimeframeSchema.options;

// ============================================================================
// Candle (OHLCV)
// ============================================================================

export const CandleSchema = z.object({
  timestamp: z.number(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
  volume: z.number(),
});

export type Candle = z.infer<typeof CandleSchema>;

// ============================================================================
// Order Types
// ============================================================================

export const OrderSideSchema = z.enum(['buy', 'sell']);
export type OrderSide = z.infer<typeof OrderSideSchema>;

export const OrderTypeSchema = z.enum(['market', 'limit']);
export type OrderType = z.infer<typeof OrderTypeSchema>;

export const OrderStatusSchema = z.enum(['pending', 'filled', 'cancelled']);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  side: OrderSideSchema,
  type: OrderTypeSchema,
  amount: z.number().positive(),
  price: z.number().positive().optional(),
  status: OrderStatusSchema,
  createdAt: z.number(),
  filledAt: z.number().optional(),
  filledPrice: z.number().positive().optional(),
  /** Human-readable reason when status is 'cancelled' due to execution failure */
  cancelReason: z.string().optional(),
});

export type Order = z.infer<typeof OrderSchema>;

// ============================================================================
// Trade Action Types
// ============================================================================

export const TradeActionSchema = z.enum([
  'OPEN_LONG',
  'CLOSE_LONG',
  'OPEN_SHORT',
  'CLOSE_SHORT',
]);
export type TradeAction = z.infer<typeof TradeActionSchema>;

// ============================================================================
// Exit Reason Taxonomy (FR-6)
// ============================================================================

/**
 * All recognised exit reason values.  Exported as a const tuple so callers can
 * build Zod enums, runtime guards, and SELECT IN lists without hard-coding strings.
 *
 * Precedence (highest → lowest):
 *   engine-managed: stop_loss > take_profit > liquidation
 *   engine-derived: signal_flip > end_of_backtest
 *   strategy-declared (via ctx.closeReason()): trail_stop, fade_exit, time_stop, oi_exit, other
 *   default: signal
 */
export const EXIT_REASONS = [
  'stop_loss',       // engine: SL price hit
  'take_profit',     // engine: TP price hit
  'trail_stop',      // strategy-declared: trailing stop exit
  'fade_exit',       // strategy-declared: fade/mean-reversion exit
  'time_stop',       // strategy-declared: time-based exit
  'oi_exit',         // strategy-declared: open-interest driven exit
  'signal_flip',     // engine-derived: close + opposite open on same bar
  'signal',          // default: plain strategy close with no further classification
  'end_of_backtest', // engine: force-closed at last bar
  'liquidation',     // engine: leveraged portfolio liquidation
  'rebalance',       // engine: cross-sectional allocator closed the name at a rebalance (dropped / flipped / no score)
  'other',           // catch-all for unrecognised strategy-declared reasons
] as const;

export type ExitReason = typeof EXIT_REASONS[number];

// ============================================================================
// Trade (Event-Based)
// ============================================================================

export const TradeSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  action: TradeActionSchema,
  price: z.number().positive(),
  amount: z.number().positive(),
  timestamp: z.number(),

  // Only for close trades
  pnl: z.number().optional(),
  pnlPercent: z.number().optional(),
  closedPositionId: z.string().optional(), // References the open trade

  // Balance tracking
  balanceAfter: z.number(),

  // Fee tracking
  fee: z.number().optional(), // Fee amount in quote currency
  feeRate: z.number().optional(), // Fee rate as decimal (0.001 = 0.1%)

  // Slippage tracking
  slippage: z.number().optional(), // Slippage cost in quote currency

  // Funding rate at trade time (futures mode only)
  fundingRate: z.number().optional(),

  // Accumulated funding income during position hold (futures mode only)
  fundingIncome: z.number().optional(),

  // Why the position was closed — full 11-value taxonomy (FR-6)
  exitReason: z.enum(EXIT_REASONS).optional(),

  // FR-6: Portfolio equity (mark-to-market) snapshot before position cost was deducted.
  // Present on OPEN trades.
  equityAtEntry: z.number().optional(),

  // FR-6: Stop-loss price that was active at the moment of entry.
  // Present on OPEN trades when an engine SL was set on the entry bar.
  initialStopPrice: z.number().optional(),

  // FR-6: Max Adverse Excursion — worst intrabar move against the position, as a
  // fraction of entry notional. Always ≤ 0. Present on CLOSE trades.
  mae: z.number().optional(),

  // FR-6: Max Favorable Excursion — best intrabar move in favour of the position,
  // as a fraction of entry notional. Always ≥ 0. Present on CLOSE trades.
  mfe: z.number().optional(),

  // Strategy that produced this trade (tagged by aggregate-engine for per-sub correlation filtering)
  strategyName: z.string().optional(),
});

export type Trade = z.infer<typeof TradeSchema>;

// ============================================================================
// Position Side
// ============================================================================

export const PositionSideSchema = z.enum(['long', 'short']);
export type PositionSide = z.infer<typeof PositionSideSchema>;

// ============================================================================
// Position (Open)
// ============================================================================

export const PositionSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  side: PositionSideSchema,
  amount: z.number().positive(),
  entryPrice: z.number().positive(),
  entryTime: z.number(),
  unrealizedPnl: z.number(),
});

export type Position = z.infer<typeof PositionSchema>;

// ============================================================================
// Equity Point
// ============================================================================

export const EquityPointSchema = z.object({
  timestamp: z.number(),
  equity: z.number(),
  drawdown: z.number(),
  drawdownPercent: z.number().optional(),
});

export type EquityPoint = z.infer<typeof EquityPointSchema>;

// ============================================================================
// Rolling Metrics
// ============================================================================

export const RollingMetricsSchema = z.object({
  timestamps: z.array(z.number()),
  cumulativeReturn: z.array(z.number()),
  drawdown: z.array(z.number()),
  rollingSharpe: z.array(z.number()),
  cumulativeWinRate: z.array(z.number()),
  cumulativeProfitFactor: z.array(z.number()),
});

export type RollingMetrics = z.infer<typeof RollingMetricsSchema>;

// ============================================================================
// Funding Rate (Futures)
// ============================================================================

export interface FundingRate {
  timestamp: number;     // Unix ms, aligned to 8h funding interval
  fundingRate: number;   // e.g. 0.0001 = 0.01%
  markPrice?: number;    // Mark price at funding time (optional)
}

// ============================================================================
// Open Interest (Futures)
// ============================================================================

export interface OpenInterestRecord {
  timestamp: number;           // Unix ms
  openInterestAmount: number;  // Open interest in base currency units
}

// ============================================================================
// Long/Short Ratio (Futures)
// ============================================================================

export interface LongShortRatioRecord {
  timestamp: number;      // Unix ms
  longRatio: number;      // e.g. 0.6 = 60% long positions
  shortRatio: number;     // e.g. 0.4 = 40% short positions
  longShortRatio: number; // longRatio / shortRatio, e.g. 1.5
}

// ============================================================================
// Alt/Macro Metric Data (for strategy context)
// ============================================================================

export interface AltMetricPoint {
  timestamp: number;  // Unix ms (daily resolution, midnight UTC)
  value: number;
}

/** A named time-series metric from an external source */
export interface AltMetricSeries {
  source: string;  // e.g. 'fred', 'defillama', 'calculated'
  metric: string;  // e.g. 'dxy', 'tvl_total', 'fear_greed'
  data: AltMetricPoint[];
}

// ============================================================================
// Backtest Diagnostics
// ============================================================================

export interface BacktestDiagnostics {
  bars: {
    /** Total candles loaded (including warmup) */
    total: number;
    /** Candles before tradeableStartIdx (warmup period) */
    warmup: number;
    /** Candles where the engine executed onBar in the tradeable period */
    active: number;
  };
  strategy: {
    /** Total onBar calls (warmup + active) */
    onBarCalls: number;
    /** Signals generated via openLong/openShort/closeLong/closeShort */
    signalsGenerated: {
      openLong: number;
      openShort: number;
      closeLong: number;
      closeShort: number;
    };
    /**
     * Counts of skip reasons recorded by strategy via ctx.skip(reason).
     * Keys are freeform strings (prefer snake_case stable keys).
     */
    skipReasons: Record<string, number>;
  };
  execution: {
    /** Total orders that were filled */
    ordersFilled: number;
    ordersRejected: {
      /** Orders rejected due to insufficient cash */
      insufficientCash: number;
      /** Orders rejected for other reasons */
      other: number;
    };
  };
  data: {
    /** Number of active bars where currentFundingRate was non-null */
    fundingRateBarsNonNull: number;
    /** Number of active bars where currentOpenInterest was non-null (using latest-before) */
    openInterestBarsNonNull: number;
    /** Number of active bars where currentLongShortRatio was non-null (using latest-before) */
    longShortRatioBarsNonNull: number;
    /**
     * Min/max of |fundingRate| over non-null bars.
     * null when fundingRateBarsNonNull = 0.
     */
    fundingRateRange: { min: number; max: number } | null;
  };
}

// ============================================================================
// Backtest Configuration
// ============================================================================

export const BacktestConfigSchema = z.object({
  id: z.string(),
  strategyName: z.string(),
  params: z.record(z.string(), z.unknown()),
  symbol: z.string(),
  timeframe: TimeframeSchema,
  startDate: z.number(),
  endDate: z.number(),
  initialCapital: z.number().positive(),
  exchange: z.string(),
  leverage: z.number().min(1).max(125).default(1).optional(),
  /** Mode for the backtest. Defaults to 'futures' (funding rates, perpetuals). Use 'spot' only for spot-market testing. */
  mode: z.enum(['spot', 'futures']).default('futures').optional(),
  /** Optional reference to a strategy_configs row for this run. */
  strategyConfigId: z.string().optional(),
  /**
   * Allow simultaneous long and short positions (hedge mode).
   * Default false: opening a short while long is open (or vice versa) throws.
   */
  allowHedge: z.boolean().default(false).optional(),
});

export type BacktestConfig = z.infer<typeof BacktestConfigSchema>;

// ============================================================================
// Performance Metrics
// ============================================================================

export const PerformanceMetricsSchema = z.object({
  totalReturn: z.number(),
  totalReturnPercent: z.number(),
  maxDrawdown: z.number(),
  maxDrawdownPercent: z.number(),
  sharpeRatio: z.number(),
  sortinoRatio: z.number(),
  calmarRatio: z.number(),
  winRate: z.number(),
  profitFactor: z.number(),
  totalTrades: z.number(),
  winningTrades: z.number(),
  losingTrades: z.number(),
  avgWin: z.number(),
  avgLoss: z.number(),
  avgWinPercent: z.number(),
  avgLossPercent: z.number(),
  expectancy: z.number(),
  expectancyPercent: z.number(),
  largestWin: z.number(),
  largestLoss: z.number(),
  avgTradeDuration: z.number(),
  exposureTime: z.number(),
  totalFees: z.number(), // Sum of all fees paid
  totalSlippage: z.number().optional(), // Sum of all slippage costs

  /**
   * SHA-28: additive +- intrabar-fill-uncertainty band, expressed as
   * percentage points of initial capital (e.g. 1.8 means +-1.8%). Reporting
   * metadata ONLY — reflects that a real exchange fill can plausibly deviate
   * from the modelled backtest fill price (empirical Bybit-fill study).
   * NEVER used to adjust totalReturnPercent or any other point-estimate
   * metric. See `calculateSlippageUncertaintyBandPercent` in
   * src/analysis/metrics.ts. Optional for backward compat with metrics
   * computed before this field existed.
   */
  slippageUncertaintyBandPercent: z.number().optional(),

  // Futures mode: breakdown of returns by source
  totalFundingIncome: z.number().optional(),
  tradingPnl: z.number().optional(),

  // Long/Short breakdown
  longPnl: z.number().optional(),
  shortPnl: z.number().optional(),
  longTrades: z.number().optional(),
  shortTrades: z.number().optional(),
  longWinRate: z.number().optional(),
  shortWinRate: z.number().optional(),

  // Engine-managed SL/TP exit counters (populated when strategy uses setStopLoss/setTakeProfit)
  engineStopLossCount: z.number().optional(),
  engineTakeProfitCount: z.number().optional(),
  /** Number of bars where both SL and TP triggered simultaneously and pessimistic fill was applied
   * (either no sub-candles available, or sub-candles showed both triggered on the same sub-bar). */
  pessimisticSlTpCount: z.number().optional(),

  /**
   * true when Sharpe and compound total return disagree in sign (e.g. positive Sharpe with a
   * net loss) — a volatility-drag artifact of arithmetic per-bar Sharpe; the metric is not wrong
   * but is misleading.  false (including when there are zero trades).
   */
  sharpeReturnSignMismatch: z.boolean().optional(),

  /**
   * Compound Annual Growth Rate expressed as a percentage (e.g. 25.4 = 25.4%/yr).
   * Formula: (finalEquity / initialEquity)^(1/years) − 1, where years is the elapsed
   * calendar time from the equity series. Guards:
   *   - years ≤ 0 (< 2 equity points) → 0
   *   - finalEquity ≤ 0 → −100%
   *   - initialEquity ≤ 0 → 0
   * Consistent with Calmar = cagrPercent / |maxDrawdownPercent|.
   */
  cagrPercent: z.number().optional(),
});

export type PerformanceMetrics = z.infer<typeof PerformanceMetricsSchema>;

// ============================================================================
// Backtest Result
// ============================================================================

export const BacktestResultSchema = z.object({
  id: z.string(),
  config: BacktestConfigSchema,
  trades: z.array(TradeSchema),
  equity: z.array(EquityPointSchema),
  metrics: PerformanceMetricsSchema,
  rollingMetrics: RollingMetricsSchema.optional(),
  /** Per-bar indicator values emitted by strategies via context.setIndicator() */
  indicators: z.record(
    z.string(),
    z.object({ timestamps: z.array(z.number()), values: z.array(z.number()) })
  ).optional(),
  /** Optional diagnostics collected when collectDiagnostics=true. Undefined otherwise. */
  diagnostics: z.custom<BacktestDiagnostics>().optional(),
  /**
   * Strategy log entries collected when the engine runs with collectStrategyLogs=true.
   * Optional — omitted in old DB rows and when collection is not requested.
   * Populated as an array of { timestamp, message } entries emitted via ctx.log().
   */
  strategyLogs: z.array(z.object({ timestamp: z.number(), message: z.string() })).optional(),
  /**
   * Per-symbol data-coverage report (BT coverage hardening, fix/bt-coverage-lifetime).
   * Keyed by symbol — a single entry for single-asset results, one entry per
   * sub-strategy symbol for aggregate results (AggregateBacktestResult extends
   * this schema). Optional/additive — omitted in old DB rows and when
   * skipCandleValidation bypassed lifetime clipping.
   */
  dataCoverage: z.record(z.string(), SymbolDataCoverageSchema).optional(),
  /**
   * Loud, non-fatal run warnings (B51): e.g. "<strategy> does not declare
   * warmupBars; legacy 200 bars applied — declare warmupBars". Surfaced in the
   * MCP job-result text. Optional/additive — omitted when there is nothing to
   * warn about and in old DB rows.
   */
  warnings: z.array(z.string()).optional(),
  createdAt: z.number(),
});

export type BacktestResult = z.infer<typeof BacktestResultSchema>;

// ============================================================================
// Utility functions for timeframe conversion
// ============================================================================

/**
 * Convert timeframe string to milliseconds
 */
export function timeframeToMs(timeframe: Timeframe): number {
  const map: Record<Timeframe, number> = {
    '1m': 60 * 1000,
    '5m': 5 * 60 * 1000,
    '15m': 15 * 60 * 1000,
    '20m': 20 * 60 * 1000,
    '30m': 30 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '4h': 4 * 60 * 60 * 1000,
    '12h': 12 * 60 * 60 * 1000,
    '18h': 18 * 60 * 60 * 1000,
    '1d': 24 * 60 * 60 * 1000,
    '3d': 3 * 24 * 60 * 60 * 1000,
    '1w': 7 * 24 * 60 * 60 * 1000,
  };
  return map[timeframe];
}

/**
 * Convert timeframe to CCXT format
 */
export function timeframeToCCXT(timeframe: Timeframe): string {
  // CCXT uses the same format for most exchanges
  return timeframe;
}
